/**
 * Global search palette.
 *
 * One box that finds records anywhere the user is allowed to look — parties,
 * products, ledger accounts, vouchers — so nobody has to know which module a
 * record lives in before they can find it.
 *
 * Results come from the server's single search endpoint, which applies the
 * same permission checks as the rest of the platform.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { get } from '@/lib/api';

interface SearchHit {
  group: string;
  type: string;
  id: number;
  title: string;
  subtitle?: string | null;
  href: string;
}

interface SearchResponse {
  query: string;
  hits: SearchHit[];
  skipped: string[];
}

interface GlobalSearchProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function GlobalSearch({ open, onOpenChange }: GlobalSearchProps) {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  const [term, setTerm] = useState('');
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [skipped, setSkipped] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cursor, setCursor] = useState(0);

  // Ctrl/Cmd-K from anywhere.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        onOpenChange(true);
      }
      if (event.key === 'Escape') onOpenChange(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onOpenChange]);

  useEffect(() => {
    if (open) {
      // Focus after the dialog has painted.
      const id = window.setTimeout(() => inputRef.current?.focus(), 10);
      return () => window.clearTimeout(id);
    }
    setTerm('');
    setHits([]);
    setError(null);
    setCursor(0);
    return undefined;
  }, [open]);

  // Debounced query. The request that comes back last is not necessarily the
  // one for what is currently typed, so stale responses are discarded.
  useEffect(() => {
    if (!open) return undefined;
    const trimmed = term.trim();

    if (trimmed.length < 2) {
      setHits([]);
      setSkipped([]);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const timer = window.setTimeout(async () => {
      try {
        const data = await get<SearchResponse>('/search', { q: trimmed });
        if (cancelled) return;
        setHits(data.hits);
        setSkipped(data.skipped ?? []);
        setCursor(0);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Search failed.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 180);

    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [term, open]);

  const go = useCallback((hit: SearchHit) => {
    onOpenChange(false);
    navigate(hit.href);
  }, [navigate, onOpenChange]);

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setCursor((c) => Math.min(c + 1, hits.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (event.key === 'Enter' && hits[cursor]) {
      event.preventDefault();
      go(hits[cursor]);
    }
  };

  if (!open) return null;

  // Group while preserving the order the server returned.
  const groups: Array<{ label: string; items: SearchHit[] }> = [];
  for (const hit of hits) {
    const existing = groups.find((g) => g.label === hit.group);
    if (existing) existing.items.push(hit);
    else groups.push({ label: hit.group, items: [hit] });
  }

  let index = -1;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-foreground/20 p-4 pt-[10vh] backdrop-blur-[2px]"
      role="dialog"
      aria-modal="true"
      aria-label="Search"
      onClick={() => onOpenChange(false)}
    >
      <div
        className="w-full max-w-xl overflow-hidden rounded-xl border border-border bg-popover shadow-raised"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2.5 border-b border-border px-3.5">
          {loading
            ? <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
            : <Search className="h-4 w-4 shrink-0 text-muted-foreground" />}
          <input
            ref={inputRef}
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search parties, products, accounts, vouchers…"
            className="h-12 flex-1 bg-transparent text-[14px] outline-none placeholder:text-muted-foreground"
          />
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            aria-label="Close search"
            className="grid h-7 w-7 place-items-center rounded text-muted-foreground hover:bg-accent"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="max-h-[55vh] overflow-y-auto py-1">
          {error && (
            <p className="px-4 py-6 text-center text-[15px] text-destructive">{error}</p>
          )}

          {!error && term.trim().length < 2 && (
            <p className="px-4 py-8 text-center text-[15px] text-muted-foreground">
              Type at least two characters.
            </p>
          )}

          {!error && term.trim().length >= 2 && !loading && hits.length === 0 && (
            <p className="px-4 py-8 text-center text-[15px] text-muted-foreground">
              Nothing found for “{term.trim()}”.
            </p>
          )}

          {groups.map((group) => (
            <div key={group.label} className="pb-1">
              <div className="px-4 pb-1 pt-2.5 text-[13px] font-semibold uppercase tracking-wider text-muted-foreground">
                {group.label}
              </div>
              {group.items.map((hit) => {
                index += 1;
                const isCursor = index === cursor;
                return (
                  <button
                    key={`${hit.type}-${hit.id}`}
                    type="button"
                    onMouseEnter={() => setCursor(hits.indexOf(hit))}
                    onClick={() => go(hit)}
                    className={cn(
                      'flex w-full flex-col items-start gap-0.5 px-4 py-2 text-left transition-colors',
                      isCursor ? 'bg-accent' : 'hover:bg-accent/60',
                    )}
                  >
                    <span className="truncate text-[15px] font-medium">{hit.title}</span>
                    {hit.subtitle && (
                      <span className="truncate text-[14px] text-muted-foreground">{hit.subtitle}</span>
                    )}
                  </button>
                );
              })}
            </div>
          ))}

          {/* Say what was not searched rather than quietly returning less. */}
          {skipped.length > 0 && (
            <p className="border-t border-border px-4 py-2 text-[13px] text-muted-foreground">
              Not searched — you do not have access: {skipped.join(', ')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
